import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import SidebarPanel from '../../components/admin/SidebarPanel';
import AgGridTable from '../../components/AgGridTable';

const PatientManagementPage = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [healthSystemFilter, setHealthSystemFilter] = useState('');
  const [healthSystems, setHealthSystems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1, totalPages: 0, totalCount: 0, pageSize: 20, hasNext: false, hasPrevious: false
  });
  const [showDetailsSidebar, setShowDetailsSidebar] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientData, setPatientData] = useState({
    cases: [], zoomVisits: [], accessLogs: [], healthSystems: []
  });
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch health systems on component mount
  useEffect(() => {
    const fetchHealthSystems = async () => {
      try {
        const response = await adminApi.getHealthSystems();
        if (response.data && response.data.success) {
          setHealthSystems(response.data.health_systems);
        } else {
          setError('Warning: Could not load health systems. Search will work across all systems.');
        }
      } catch (err) {
        setError('Warning: Could not load health systems. Search will work across all systems.');
      }
    };

    fetchHealthSystems();
  }, []);

  // Handle search functionality
  const handleSearch = async (page = 1) => {
    if (!searchQuery.trim()) {
      setError('Please enter a search query');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the search API with pagination
      const response = await adminApi.searchPatients(
        healthSystemFilter || '',
        searchQuery.trim(),
        page,
        pagination.pageSize || 20
      );

      if (response.data && response.data.success) {
        setPatients(response.data.patients || []);
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.current_page,
            totalPages: response.data.pagination.total_pages,
            totalCount: response.data.pagination.total_count,
            pageSize: response.data.pagination.page_size,
            hasNext: response.data.pagination.has_next,
            hasPrevious: response.data.pagination.has_previous
          });
        }
      } else {
        setError(response.data?.message || 'Failed to search patients');
        setPatients([]);
        setPagination(prev => ({ ...prev, totalCount: 0, totalPages: 0 }));
      }
    } catch (err) {
      setError(err.message || 'An error occurred while searching patients');
      setPatients([]);
      setPagination(prev => ({ ...prev, totalCount: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  // Handle URL parameter - automatically open sidebar if patientId is in URL
  useEffect(() => {
    const loadPatientFromUrl = async () => {
      if (!patientId || selectedPatient) return;

      // First check if patient is already in the loaded patients list
      const patient = patients.find(p => p.id === patientId || p.patient_id === patientId);
      if (patient) {
        handleViewDetailsClick(patient);
        return;
      }

      // If not in list and patients array is empty, fetch patient directly by ID
      if (patients.length === 0) {
        try {
          setShowDetailsSidebar(true);
          // Fetch patient details - this will also set selectedPatient
          await fetchPatientDetails(patientId);
        } catch (err) {
          console.error('Error loading patient from URL:', err);
          setError('Failed to load patient details');
          navigate('/admin/patients');
        }
      }
    };

    loadPatientFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, patients.length, selectedPatient]);

  // Handle loading patients by health system
  const handleLoadHealthSystemPatients = async () => {
    if (!healthSystemFilter) {
      setError('Please select a health system');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await adminApi.getHealthSystemPatients(healthSystemFilter);
      
      if (response.data && response.data.success) {
        setPatients(response.data.patients || []);
      } else {
        setError(response.data?.message || 'Failed to load patients');
        setPatients([]);
      }
    } catch (err) {
      console.error('Error loading health system patients:', err);
      setError(err.message || 'An error occurred while loading patients');
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };


  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Custom Cell Renderer for Actions column
  const ActionsCellRenderer = (props) => {
    const { data } = props;
    if (!data) return null;

    return (
      <div className="btn-group btn-group-sm" role="group">
        <button
          className="btn btn-outline-primary"
          onClick={() => handleViewDetailsClick(data)}
          title="View Details"
        >
          <i className="fas fa-eye me-1"></i>
          View
        </button>
      </div>
    );
  };

  // ag-Grid Column Definitions
  const [columnDefs] = useState([
    {
      headerName: 'Name',
      field: 'name',
      width: 180,
      valueGetter: (params) => {
        if (params.data) {
          return `${params.data.first_name || ''} ${params.data.last_name || ''}`.trim();
        }
        return '';
      },
      cellRenderer: (params) => {
        if (params.data) {
          return (
            <a
              href="#"
              className="text-body fw-bold"
              onClick={(e) => {
                e.preventDefault();
                handleViewDetailsClick(params.data);
              }}
            >
              {`${params.data.first_name} ${params.data.last_name}`}
            </a>
          );
        }
        return null;
      }
    },
    {
      headerName: 'Core ID',
      field: 'patient_id',
      width: 120,
      valueGetter: (p) => p.data?.patient_id || 'N/A'
    },
    {
      headerName: 'Email',
      field: 'email',
      width: 200,
      valueGetter: (p) => p.data?.email || 'N/A'
    },
    {
      headerName: 'Phone',
      field: 'phone',
      width: 130,
      valueGetter: (p) => p.data?.phone || 'N/A'
    },
    {
      headerName: 'Health System',
      field: 'health_system',
      width: 180,
      valueGetter: (params) => params.data?.health_system?.name || 'N/A',
      cellRenderer: (params) => {
        if (params.data?.health_system) {
          return (
            <Link
              to={`/admin/health-systems/${params.data.health_system.health_system_id}`}
              className="text-body fw-bold"
            >
              {params.data.health_system.name}
            </Link>
          );
        }
        return 'N/A';
      }
    },
    {
      headerName: 'Created',
      field: 'created_at',
      width: 160,
      valueGetter: (p) => formatDate(p.data?.created_at)
    },
    {
      headerName: 'Updated',
      field: 'updated_at',
      width: 160,
      valueGetter: (p) => formatDate(p.data?.updated_at)
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
      minWidth: 100,
      sortable: false,
      filter: false
    }
  ]);

  const handleViewDetailsClick = async (patient) => {
    setSelectedPatient(patient);
    setShowDetailsSidebar(true);
    await fetchPatientDetails(patient.patient_id);
    // Navigate to URL with database ID (for URL routing)
    navigate(`/admin/patients/${patient.id}`);
  };

  const handleCloseSidebar = () => {
    setShowDetailsSidebar(false);
    setSelectedPatient(null);
    setPatientData({
      cases: [],
      zoomVisits: [],
      accessLogs: [],
      healthSystems: []
    });
    // Navigate back to base URL to remove the ID from URL
    navigate('/admin/patients');
  };

  const fetchPatientDetails = async (patientId) => {
    setLoadingDetails(true);
    setError(null);

    try {
      // Fetch patient record and associated data in parallel
      const [patientResponse, casesResponse, healthSystemsResponse, zoomVisitsResponse, accessLogsResponse] = await Promise.all([
        adminApi.getPatientDetails(patientId),
        adminApi.getPatientCases(patientId),
        adminApi.getPatientHealthSystems(patientId),
        adminApi.getPatientZoomVisits(patientId),
        adminApi.getPatientAccessLogs(patientId, { page: 1 }),
      ]);

      // Update selected patient with fetched data
      if (patientResponse.data && patientResponse.data.success) {
        setSelectedPatient(patientResponse.data.patient);
      }

      setPatientData({
        cases: casesResponse.data.success ? casesResponse.data.cases : [],
        healthSystems: healthSystemsResponse.data.success ? healthSystemsResponse.data.health_systems : [],
        zoomVisits: zoomVisitsResponse.data.success ? zoomVisitsResponse.data.zoom_visits : [],
        accessLogs: accessLogsResponse.data.success ? accessLogsResponse.data.data : [],
      });
    } catch (err) {
      console.error('Error fetching patient details:', err);

      // Handle 404 - patient not found
      if (err.response?.status === 404) {
        const errorMessage = err.response?.data?.detail || 'Patient not found';
        setError(`Patient not found. ${errorMessage === 'No Patient matches the given query.' ? 'The patient may have been deleted or the ID is invalid.' : errorMessage}`);
        setShowDetailsSidebar(false);
        setSelectedPatient(null);
        navigate('/admin/patients');
        return;
      }

      // Handle other errors
      setError('Failed to load patient details: ' + (err.response?.data?.detail || err.response?.data?.message || err.message));
      throw err;
    } finally {
      setLoadingDetails(false);
    }
  };


  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Patient Management</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
                <li className="breadcrumb-item active">Patients</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <button
            className="btn btn-light"
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className="fas fa-filter me-1"></i> {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filters Section */}
      {showFilters && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Search & Filter Patients</h5>
            <div className="row align-items-end">
              <div className="col-md-4 mb-3 d-flex">
                {/* <label htmlFor="healthSystemFilter" className="form-label">Health System (Optional)</label> */}
                <select id="healthSystemFilter" className="form-select" value={healthSystemFilter} onChange={(e) => setHealthSystemFilter(e.target.value)}> 
                  <option value="">Select Health System (Optional)</option> {healthSystems.map(hs => ( <option key={hs.id} value={hs.health_system_id}> {hs.name} </option> ))}
                </select>
              </div>
              <div className="col-md-4 mb-3 d-flex">
                {/* <label htmlFor="patientSearch" className="form-label">Search Patients</label> */}
                <input type="text" id="patientSearch" className="form-control" placeholder="Search by name, ID, email, phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </div>
              <div className="col-md-4 mb-3 d-flex gap-2">
                <button className="btn btn-primary" onClick={() => handleSearch()} hidden={!searchQuery.trim()} title={!searchQuery.trim() ? "Enter search terms to enable search" : "Search for patients"} ><i className='fa fa-search'></i></button>
                {/* {healthSystemFilter && searchQuery && (
                  <button className="btn text-secondary" style={{paddingLeft:0}} >Search within {healthSystems.find(hs => hs.health_system_id === healthSystemFilter)?.name}</button>
                )} */}
                <button className="btn btn-info" onClick={handleLoadHealthSystemPatients} hidden={!healthSystemFilter} title={!healthSystemFilter ? "Select a health system to enable load all" : "Load all patients from selected health system"}>Show all</button> 
                  {/* <button className="btn btn-secondary" onClick={handleClearFilters}><i className="fas fa-times me-1"></i></button> */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Patients Table */}
      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading patients...</p>
            </div>
          ) : patients.length === 0 ? (
            <div className="alert text-center text-secondary">
              {searchQuery ?
                `No patients found matching "${searchQuery}"${healthSystemFilter ? ` in ${healthSystems.find(hs => hs.health_system_id === healthSystemFilter)?.name}` : ''}. Try a different search term.` :
                'Use the search function or select a health system and click "Show All" to view patients.'
              }
            </div>
          ) : (
            <>
              <AgGridTable
                rowData={patients}
                columnDefs={columnDefs}
                defaultColDef={{
                  flex: 1,
                  minWidth: 100,
                  resizable: true,
                  sortable: true,
                  filter: true,
                }}
                gridOptions={{
                  autoSizeStrategy: {
                    type: 'fitGridWidth'
                  }
                }}
                getRowId={(params) => `${params.data.id}`}
              />
            </>
          )}
        </div>
      </div>

      {/* Patient Details Sidebar */}
      <SidebarPanel
        isOpen={showDetailsSidebar}
        onClose={handleCloseSidebar}
        title="Patient Details"
      >
        {selectedPatient && (
          <>
            {loadingDetails ? (
                    <div className="text-center my-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading details...</span>
                      </div>
                      <p className="mt-2">Loading patient details...</p>
                    </div>
                  ) : (
                    <>
                      <div className="row">
                        <div className="col-xl-12">
                          <div className="card overflow-hidden">
                            <div className="card-body">
                              <div className="d-flex card-title justify-content-between align-items-center mb-3">
                                <h5 className="text-primary mb-0">{selectedPatient.first_name} {selectedPatient.last_name}</h5>
                                <button className="btn btn-primary disabled btn-sm" title="Edits can be done via API or Initiate Visit function">
                                  <i className="fa fa-pencil me-1"></i> Edit
                                </button>
                              </div>
                              <div className="row">
                                <div className="col-sm-12">
                                  <div className="pt-4">
                                    <div className="row">
                                      <div className="col-6">
                                        <h5 className="font-size-15">Full Name:</h5>
                                        <p className="text-muted mb-0">{selectedPatient.first_name} {selectedPatient.middle_name ? selectedPatient.middle_name + ' ' : ''}{selectedPatient.last_name}</p>
                                      </div>
                                      <div className="col-6">
                                        <h5 className="font-size-15">Date of Birth:</h5>
                                        <p className="text-muted mb-0">{selectedPatient.dob || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="row mt-4">
                                      <div className="col-6">
                                        <h5 className="font-size-15">Email:</h5>
                                        <p className="text-muted mb-0">{selectedPatient.email || 'N/A'}</p>
                                      </div>
                                      <div className="col-6">
                                        <h5 className="font-size-15">Phone:</h5>
                                        <p className="text-muted mb-0">{selectedPatient.phone || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="row mt-4">
                                      <div className="col-6">
                                        <h5 className="font-size-15">Gender:</h5>
                                        <p className="text-muted mb-0">{selectedPatient.gender || 'N/A'}</p>
                                      </div>
                                      <div className="col-6">
                                        <h5 className="font-size-15">Medical Record #:</h5>
                                        <p className="text-muted mb-0">{selectedPatient.ehr_medical_record_number || 'N/A'}</p>
                                      </div>
                                    </div>
                                    {selectedPatient.phone_2 && (
                                      <div className="mt-4">
                                        <h5 className="font-size-15">Alternate Phone:</h5>
                                        <p className="text-muted mb-0">{selectedPatient.phone_2}</p>
                                      </div>
                                    )}
                                    {selectedPatient.street_address && (
                                      <div className="mt-4">
                                        <h5 className="font-size-15">Address:</h5>
                                        <p className="text-muted mb-0">
                                          {selectedPatient.street_address}
                                          {selectedPatient.street_address_2 && `, ${selectedPatient.street_address_2}`}
                                          {selectedPatient.city && `, ${selectedPatient.city}`}
                                          {selectedPatient.state && `, ${selectedPatient.state}`}
                                          {selectedPatient.zip_code && ` ${selectedPatient.zip_code}`}
                                        </p>
                                      </div>
                                    )}
                                    <div className="mt-4">
                                      <h5 className="font-size-15">Created At:</h5>
                                      <p className="text-muted mb-0">{formatDate(selectedPatient.created_at)}</p>
                                    </div>
                                    <div className="mt-4">
                                      <h5 className="font-size-15">Last Updated:</h5>
                                      <p className="text-muted mb-0">{formatDate(selectedPatient.updated_at)}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Associated Health System Card */}
                          {patientData.healthSystems.length > 0 && (
                            <div className="card">
                              <div className="card-body">
                                <h4 className="card-title text-primary mb-4">Associated Health System</h4>
                                <p className="mb-0"><strong>Name:</strong> {patientData.healthSystems[0].name}</p>
                                <p className="mb-0"><strong>ID:</strong> {patientData.healthSystems[0].health_system_id}</p>
                              </div>
                            </div>
                          )}
                        </div>

                    {/* Related data - Cases */}
                    <div className="col-xl-12">
                      <div className="card">
                        <div className="card-body">
                          <h4 className="card-title text-primary mb-4">Associated Cases</h4>
                          {loadingDetails ? (
                            <div className="text-center my-3">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading cases...</span>
                              </div>
                              <p className="mt-2">Loading cases...</p>
                            </div>
                          ) : patientData.cases.length === 0 ? (
                            <div className="alert text-center text-secondary">
                              No cases found for this patient.
                            </div>
                          ) : (
                            <AgGridTable
                              rowData={patientData.cases}
                              height="100%"
                              columnDefs={[
                                {
                                  headerName: 'Case ID',
                                  field: 'CoreServer_CaseID',
                                  width: 150,
                                  valueGetter: (p) => p.data?.CoreServer_CaseID || p.data?.id || ''
                                },
                                {
                                  headerName: 'Provider',
                                  field: 'provider_info_fk',
                                  flex: 1,
                                  valueGetter: (p) => {
                                    if (!p.data?.provider_info_fk) return 'N/A';
                                    return `${p.data.provider_info_fk.first_name} ${p.data.provider_info_fk.last_name}`;
                                  }
                                },
                                {
                                  headerName: 'Date',
                                  field: 'created_at',
                                  width: 150,
                                  valueGetter: (p) => p.data?.created_at ? formatDate(p.data.created_at) : ''
                                },
                                {
                                  headerName: 'Status',
                                  field: 'mira_sol_case_status',
                                  width: 150
                                },
                                {
                                  headerName: 'Action',
                                  field: 'action',
                                  width: 100,
                                  sortable: false,
                                  filter: false,
                                  cellRenderer: (params) => {
                                    if (!params.data) return null;
                                    return (
                                      <Link to={`/admin/cases/${params.data.id}/audit-logs`} className="btn btn-info btn-sm">View</Link>
                                    );
                                  }
                                }
                              ]}
                              defaultColDef={{
                                resizable: true,
                                sortable: true,
                                filter: true,
                              }}
                              gridOptions={{
                                domLayout: 'autoHeight',
                                autoSizeStrategy: { type: 'fitGridWidth' }
                              }}
                              getRowId={(params) => `${params.data.id}`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Related data - Access Logs */}
                    <div className="col-xl-12">
                      <div className="card">
                        <div className="card-body">
                          <h4 className="card-title text-primary mb-4">Audit Trail</h4>
                          {loadingDetails ? (
                            <div className="text-center my-3">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading logs...</span>
                              </div>
                              <p className="mt-2">Loading logs...</p>
                            </div>
                          ) : patientData.accessLogs.length === 0 ? (
                            <div className="alert text-center text-secondary">
                              No logs found for this patient.
                            </div>
                          ) : (
                            <AgGridTable
                              rowData={patientData.accessLogs}
                              height="100%"
                              columnDefs={[
                                {
                                  headerName: 'Timestamp',
                                  field: 'timestamp',
                                  width: 180,
                                  valueGetter: (p) => p.data?.timestamp ? formatDate(p.data.timestamp) : ''
                                },
                                {
                                  headerName: 'Access Type',
                                  field: 'access_type',
                                  width: 150
                                },
                                {
                                  headerName: 'Result',
                                  field: 'result',
                                  width: 120
                                },
                                {
                                  headerName: 'Details',
                                  field: 'details',
                                  width: 400,
                                  pinned: 'right',
                                  lockPinned: true,
                                  autoHeight: true,
                                  wrapText: true,
                                  filter: false,
                                  valueGetter: (p) => {
                                    if (!p.data) return '';
                                    const { timestamp, access_type, result, ...otherDetails } = p.data;
                                    return JSON.stringify(otherDetails, null, 2);
                                  },
                                  cellRenderer: (params) => {
                                    if (!params.data) return null;
                                    const { timestamp, access_type, result, ...otherDetails } = params.data;
                                    const detailsStr = JSON.stringify(otherDetails, null, 2);
                                    return (
                                      <div className="d-flex align-items-start gap-2">
                                        <div style={{ maxHeight: '100px', overflowY: 'auto', flex: 1 }}>
                                          <pre className="mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>{detailsStr}</pre>
                                        </div>
                                        <button
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={() => navigator.clipboard.writeText(detailsStr)}
                                          title="Copy to clipboard"
                                        >
                                          <i className="fas fa-copy"></i>
                                        </button>
                                      </div>
                                    );
                                  }
                                }
                              ]}
                              defaultColDef={{
                                resizable: true,
                                sortable: true,
                                filter: true,
                              }}
                              gridOptions={{
                                domLayout: 'autoHeight',
                                suppressHorizontalScroll: false
                              }}
                              getRowId={(params) => `${params.data.timestamp}-${params.data.access_type}`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                    </>
                  )}
          </>
        )}
      </SidebarPanel>
    </>
  );
};

export default PatientManagementPage;